import { computed, ref, type Ref } from 'vue'

import { useRealtimeSubscription, type RealtimeFrame } from '@ja/realtime'

import type { CrudEvent } from './useCrudChannel'

export interface UseViewerRealTimeOptions {
  modelName: string
  recordId: string | number
  currentUserId: string | number | null
  onUpdated?: (newData: Record<string, unknown>, actorId: string | number | null) => void
  onDeleted?: () => void
}

export interface UseViewerRealTimeReturn {
  isConnected: Ref<boolean>
  lastUpdatedAt: Ref<string | null>
  lastUpdatedBy: Ref<string | number | null>
  unsubscribe: () => void
}

/**
 * Live-updates subscription for the Viewer page.
 *
 * Subscribes to the per-record `crud:<modelName>:<id>` channel and surfaces
 * `crud.updated` / `crud.deleted` frames to the page.
 */
export function useViewerRealTime(options: UseViewerRealTimeOptions): UseViewerRealTimeReturn {
  const { modelName, recordId, currentUserId, onUpdated, onDeleted } = options

  const lastUpdatedAt = ref<string | null>(null)
  const lastUpdatedBy = ref<string | number | null>(null)

  const channel = computed(() => `crud:${modelName}:${recordId}`)

  function isOwnEvent(event: CrudEvent): boolean {
    return (
      currentUserId !== undefined &&
      currentUserId !== null &&
      event.actorId === currentUserId
    )
  }

  const { isConnected, unsubscribe } = useRealtimeSubscription<CrudEvent>(channel, {
    onMessage(frame: RealtimeFrame<CrudEvent>) {
      if (frame.event === 'crud.updated') {
        const event = frame.data as CrudEvent
        if (isOwnEvent(event)) return
        lastUpdatedAt.value = event.timestamp
        lastUpdatedBy.value = event.actorId
        if (event.data) {
          onUpdated?.(event.data, event.actorId)
        }
      } else if (frame.event === 'crud.deleted') {
        const event = frame.data as CrudEvent
        if (isOwnEvent(event)) return
        onDeleted?.()
      }
    },
  })

  return {
    isConnected,
    lastUpdatedAt,
    lastUpdatedBy,
    unsubscribe,
  }
}
